// The two screens that answer "what needs doing today" — the board, its tasks, and the
// dashboard that reports the period.
//
// Pure data — no imports, ever. errors.js reads he.leads.needContact at module scope,
// so a part reaching back for it would hand that read a half-built object. See he.js.
const dashboard = {
  dashboard: {
    title: 'לוח בקרה',
    totalProfit: 'רווח נקי',
    officeExpenses: 'הוצאות משרד',
    netAfterOffice: 'רווח לאחר הוצאות משרד',
    outstanding: 'יתרות לגבייה',
    overdue: 'חשבוניות באיחור',
    openTasks: 'משימות פתוחות',
    balance: 'הפער בין השותפים',
    // Same rule as the settlements screen: a direction, never a debt. Nobody pays.
    noGap: 'אין פער בין השותפים',
    gapFavours: 'הפער לטובת',
    gapAgainst: 'מול',
    // The few units most responsible for the balance. Explicitly a partial list, never
    // a breakdown: it carries no total, and the note says where the full one lives.
    balanceTopUnits: 'מה מרכיב את המאזן',
    // Points at the settlement screen for what is ACTUALLY there. It used to promise
    // "הפירוט המלא, שמסתכם למאזן" — the per-unit table that screen no longer has, since
    // it states the gap as five plain figures per partner instead. A tile that sends a
    // reader looking for a table that is gone is worse than one that sends them nowhere.
    balanceMoreUnits: (count) => `ועוד ${count} יחידות`,
    balanceFullBreakdown: 'למסך ההתחשבנות',
    unpaidExpenses: 'הוצאות שטרם שולמו',
    unpaidExpensesList: 'הוצאות שהתחייבנו אליהן והכסף טרם יצא',
    overdueList: 'חשבוניות שחרגו מתנאי התשלום',
    missingInvoices: 'חשבוניות ריטיינר שחסרות החודש',
    // The two zones. The snapshot heading says "as of today" so a chosen month is
    // never read as applying to figures that describe the present.
    periodSection: 'תוצאות התקופה',
    snapshotSection: 'מצב נוכחי',
    allTime: 'כל הזמן',
    allTimeLabel: 'כל הזמן',
    prevMonth: 'לחודש הקודם',
    nextMonth: 'לחודש הבא',
    thisMonth: 'החודש הנוכחי',
    // Forward-looking retainer revenue. The badge and note exist to make it
    // impossible to read as money earned or money owed.
    forecastTitle: 'צפי הכנסות מריטיינרים',
    forecastBadge: 'תחזית',
    // The count beside the forecast total, and it counts RETAINERS — only a retainer is
    // forecast (calculations/retainerForecast). It read "עבודות", which was already off
    // by one concept and became actively misleading once עבודה started meaning the jobs
    // BENEATH a retainer: "12 עבודות" under a heading about retainer revenue invites
    // exactly the wrong reading.
    forecastJobs: 'ריטיינרים',
    forecastEmpty: 'אין ריטיינרים צפויים לחודש זה',
  },
  tasks: {
    title: 'המשימות שלי',
    newTask: 'משימה חדשה',
    editTask: 'עריכת משימה',
    // A task with no client: office work, the owners' own branding.
    internal: 'פנימי',
    // The line that follows "בוצע" for five seconds (components/DoneUndo).
    markedDone: 'סומנה כבוצעה',
    undo: 'ביטול',
    taskTitle: 'כותרת',
    description: 'תיאור',
    dueDate: 'תאריך יעד',
    // תזמון: תאריך היעד הוא מה שמפעיל את הפעמון ואת הסימון האדום בתפריט, ולכן ההסבר
    // יושב ליד השדה — לא מדובר בתיוג אלא בתאריך שקורה בו משהו.
    schedule: 'לחזור לזה בעוד',
    quickTomorrow: 'מחר',
    quickInDays: (n) => `${n} ימים`,
    quickNone: 'בלי תאריך',
    dueToday: 'היום',
    markDone: 'סימון כבוצע',

    reopen: 'החזרה לפתוח',
    done: 'בוצע',
    auto: 'אוטומטי',
    // מחיקת משימה. הדיאלוג קצר בכוונה: משימה היא שורה אחת שלא תלוי בה שום דבר, בניגוד
    // לריטיינר או לעבודה שהמחיקה שלהם גוררת היסטוריית חיוב שלמה.
    deleteTitle: 'מחיקת משימה',
    deleteQuestion: 'למחוק את המשימה לצמיתות?',
  },
  board: {
    title: 'לוח משימות',
    newTask: 'משימה חדשה',
    all: 'כל המשימות',
    mine: 'שלי',
    client: 'לקוח',
    job: 'עבודה (אופציונלי)',
    noJob: 'ללא עבודה',
    assignee: 'אחראי/ת',
    unassigned: 'ללא',
    taskTitle: 'משימה',
    jobLabel: 'עבודה',
    assigneeLabel: 'אחראי',
    due: 'יעד',
    note: 'סטטוס / הערה',
    addNote: '＋ הוסף סטטוס',
    notePlaceholder: 'למשל: ממתין לחומר מתמר, בטיפול קובי, טרם שולם…',
    addTask: 'הוספת משימה',
    markDone: 'בוצע',
    owed: 'לגבייה',
    overdue: 'באיחור',
    // One control for the whole board: it collapses while anything is still open,
    // and expands once everything is shut.
    collapseAll: 'כווץ הכל',
    expandAll: 'פתח הכל',
    // The board shows open work; "בוצעו" shows what was finished in the last 90 days.
    statusOpen: 'פתוחות',
    statusDone: 'בוצעו',
    emptyDone: 'אין משימות שבוצעו.',
    emptyGroupTasks: 'אין משימות ללקוח זה.',
    emptyInternalTasks: 'אין משימות פנימיות.',
    // Which work the board shows: client tasks, internal tasks, or both.
    scopeAll: 'הכל',
    scopeClients: 'לקוחות',
    scopeInternal: 'פנימי',
    empty: 'אין משימות פתוחות. הוסיפו משימה ללקוח כדי להתחיל.',
    noneMatch: 'אין לקוחות שתואמים לסינון.',
    completedOn: 'הושלם',
  },
};

export default dashboard;
